<template>
  <div
    class="gallery-item"
    :class="itemClasses"
    :style="computedContainerStyle"
    :data-work-slug="image.slug"
    :tabindex="clickable ? 0 : undefined"
    :role="clickable ? 'button' : undefined"
    :aria-label="clickable ? (image.name || image.alt || 'Gallery image') : undefined"
    @touchstart.passive="onTouchStart"
    @touchend="onTouchEnd"
    @mouseenter="prefetchFullImage"
    @focus="prefetchFullImage"
    @click="clickable && $emit('click', image)"
    @keydown.enter.prevent="clickable && $emit('click', image)"
    @keydown.space.prevent="clickable && $emit('click', image)"
  >
    <img
      :src="thumbSrc"
      :alt="altText"
      :loading="loading"
      :fetchpriority="fetchPriority"
      decoding="async"
      :class="imgClass"
      @load="handleLoad"
      @error="handleThumbError"
    >

    <div v-if="effectiveLoaded && showSoldBadge && image.isSold" class="sold-dot" title="Sold" />

    <div v-if="effectiveLoaded && overlayStyle === 'bottom'" class="gallery-overlay gallery-overlay-bottom">
      <div class="overlay-title" :style="{ fontSize: titleFontSize + 'px' }">{{ image.name || image.alt || '' }}</div>
      <div v-if="showCaption && image.caption" class="overlay-caption">{{ image.caption }}</div>
      <div v-else-if="!showCaption && showOverlaySubtext && overlaySubtext" class="overlay-subtext">{{ overlaySubtext }}</div>
      <div v-if="showSoldBadge && image.isSold" class="overlay-sold"><span class="sold-badge">Sold</span></div>
      <div v-if="isPriceShown" class="overlay-price">
        {{ image.price.value }} {{ image.price.currency }}
      </div>
    </div>

    <div v-if="effectiveLoaded && overlayStyle === 'full'" class="gallery-overlay gallery-overlay-full">
      <div class="overlay-content">
        <div class="overlay-title" :style="{ fontSize: titleFontSize + 'px' }">{{ image.name || image.alt || '' }}</div>
        <div v-if="showCaption && image.caption" class="overlay-caption">{{ image.caption }}</div>
        <div v-else-if="!showCaption && showOverlaySubtext && overlaySubtext" class="overlay-subtext">{{ overlaySubtext }}</div>
        <div v-if="showSoldBadge && image.isSold" class="overlay-sold"><span class="sold-badge">Sold</span></div>
        <div v-if="isPriceShown" class="overlay-price">
          {{ image.price.value }} {{ image.price.currency }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mark, summary } from '@/utils/perfMarks'
import { directGcsUrl, wrapperToDirectUrl, bestThumbUrl } from '@/utils/imageUrl'
import { store } from '@/store'
import { normalizeTagGroups, artworkAlt } from '@/utils/tags'

let _firstThumbMarked = false
function markFirstThumb() {
  if (_firstThumbMarked) return
  _firstThumbMarked = true
  mark('thumbs:first:rendered')
  summary('visitor boot → first thumb')
}
export function resetFirstThumbMark() {
  _firstThumbMarked = false
}

// One-time snapshot of the thumbs index.html preloaded (injected before Vue boots)
let _preloadedThumbs = null
function isThumbPreloaded(url) {
  if (!_preloadedThumbs) {
    _preloadedThumbs = new Set(
      [...document.querySelectorAll('link[rel="preload"][as="image"]')].map(link => link.href)
    )
  }
  return _preloadedThumbs.has(url)
}
export function resetPreloadedThumbs() {
  _preloadedThumbs = null
}

export default {
  name: 'GalleryItem',

  props: {
    image: {
      type: Object,
      required: true
    },
    isLoaded: {
      type: Boolean,
      default: false
    },
    // How the image should fill the container:
    // 'bg'      - grid: hidden preloader img + background-image on container after load
    // 'natural' - masonry: visible img, width:100%; height:auto
    // 'cover'   - rows: visible img, width:100%; height:100%; object-fit:cover
    imgMode: {
      type: String,
      default: 'bg',
      validator: (v) => ['bg', 'natural', 'cover'].includes(v)
    },
    loading: {
      type: String,
      default: 'lazy',
      validator: (v) => ['lazy', 'eager'].includes(v)
    },
    // 'high' for the first above-the-fold thumbs (likely LCP element) so they don't
    // queue behind other requests; 'auto' for the rest.
    fetchPriority: {
      type: String,
      default: 'auto',
      validator: (v) => ['high', 'auto', 'low'].includes(v)
    },
    // Dimensions / margins from the parent layout component
    containerStyle: {
      type: Object,
      default: () => ({})
    },
    // Appearance
    clickable: {
      type: Boolean,
      default: true
    },
    overlayStyle: {
      type: String,
      default: 'none'
    },
    overlaySubtext: {
      type: String,
      default: ''
    },
    showOverlaySubtext: {
      type: Boolean,
      default: true
    },
    showSoldBadge: {
      type: Boolean,
      default: true
    },
    titleFontSize: {
      type: Number,
      default: 18
    },
    hoverEffect: {
      type: String,
      default: 'none'
    },
    imageStyle: {
      type: String,
      default: 'none'
    },
    showCaption: {
      type: Boolean,
      default: false
    },
    showPriceInOverlay: {
      type: Boolean,
      default: false
    },
    showPriceWhenSold: {
      type: Boolean,
      default: false
    },
    pinnedHoverId: {
      type: [String, Number],
      default: null
    }
  },

  emits: ['click', 'load'],

  data() {
    return {
      directFailed: false, // direct GCS thumb 403'd - fall back to the wrapper URL
      preloadedInHead: false // preloaded thumb - skip the loading gate + fade
    }
  },

  computed: {
    altText() {
      return artworkAlt(this.image, normalizeTagGroups(store.artistConfig?.artist?.tagGroups)) || this.image.alt || 'Artwork'
    },
    isPriceShown() {
      const { price, isSold } = this.image
      return !!(this.showPriceInOverlay && price?.isPriceShown && price?.value && (!isSold || this.showPriceWhenSold))
    },
    // Parent's load-event map, or already cached via preload link
    effectiveLoaded() {
      return this.isLoaded || this.preloadedInHead
    },
    // The wrapper thumb URL the work stores (the fallback / source of truth).
    wrapperThumb() {
      return this.image.thumbnailUrl || this.image.imageThumb || ''
    },
    // Prefer the fast direct GCS thumb; fall back to the wrapper on error.
    thumbSrc() {
      if (this.directFailed) return this.wrapperThumb
      return bestThumbUrl(this.image)
    },
    itemClasses() {
      return [
        this.effectiveLoaded ? 'loaded' : 'loading',
        this.clickable ? 'clickable' : '',
        this.effectiveLoaded && this.hoverEffect !== 'none' ? 'hover-' + this.hoverEffect : '',
        this.imageStyle !== 'none' ? 'style-' + this.imageStyle : '',
        this.pinnedHoverId != null && this.pinnedHoverId === this.image.id ? 'hover-pinned' : ''
      ]
    },
    imgClass() {
      if (this.imgMode === 'bg') return 'gallery-img-preloader'
      if (this.imgMode === 'cover') return 'gallery-img-cover'
      return 'gallery-img-natural'
    },
    computedContainerStyle() {
      if (this.imgMode === 'bg' && this.effectiveLoaded) {
        return {
          ...this.containerStyle,
          backgroundImage: `url(${this.thumbSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }
      }
      return this.containerStyle
    }
  },

  created() {
    this.preloadedInHead = isThumbPreloaded(this.thumbSrc)
  },

  methods: {
    handleLoad(event) {
      let aspectRatio = null
      if (event?.target?.naturalWidth && event.target.naturalHeight) {
        aspectRatio = event.target.naturalWidth / event.target.naturalHeight
      }
      if (event?.type !== 'error') {
        markFirstThumb()
      }
      this.$emit('load', { id: this.image.id, aspectRatio })
    },
    // Direct GCS thumb failed (e.g. object not public): retry with the wrapper URL once, then
    // treat as loaded (same as before) so the grid layout still resolves.
    handleThumbError(event) {
      if (!this.directFailed && this.thumbSrc !== this.wrapperThumb) {
        this.directFailed = true
        return
      }
      this.handleLoad(event)
    },
    // See docs/features/gallery-interactions.md — "Mobile tap detection" for why custom touch
    // handling is needed instead of relying solely on the browser's click synthesis.
    // Warms the browser's cache for the full-size detail image before the click/tap
    // completes, so WorkModal's image is often already loaded by the time it opens.
    prefetchFullImage() {
      if (!this.clickable || this._fullPrefetched) return
      // Prefetch the direct GCS full image (fast path WorkModal will also use), wrapper fallback.
      const url = (this.image.imageRef ? directGcsUrl(this.image.imageRef) : wrapperToDirectUrl(this.image.imageFile)) || this.image.imageFile
      if (!url) return
      this._fullPrefetched = true
      new Image().src = url
    },
    onTouchStart(event) {
      this.prefetchFullImage()
      this._tapTime = Date.now()
      if (event.touches.length === 1) {
        this._tapX = event.touches[0].clientX
        this._tapY = event.touches[0].clientY
      } else {
        this._tapX = null
      }
    },
    onTouchEnd(event) {
      if (!this.clickable || event.changedTouches.length !== 1) return
      const t = event.changedTouches[0]
      // _tapX is null (set by onTouchStart for multi-touch) or undefined (touchstart was
      // swallowed by the compositor for an inertia stop-tap). Use loose != null to catch both.
      // No start data → treat distance as 0 and skip elapsed check (accept as tap).
      const hasStart = this._tapX != null
      const dx = hasStart ? Math.abs(t.clientX - this._tapX) : 0
      const dy = hasStart ? Math.abs(t.clientY - this._tapY) : 0
      const elapsed = Date.now() - (this._tapTime ?? 0)
      this._tapX = null
      if (Math.sqrt(dx * dx + dy * dy) < 30 && (!hasStart || elapsed < 400)) {
        event.preventDefault()
        this.$emit('click', this.image)
      }
    }
  }
}
</script>

<style scoped>
.gallery-item {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius);
  opacity: 0;
  transition: opacity 0.4s ease-in;
}

.gallery-item.clickable {
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;

  &:focus-visible {
    outline: 3px solid var(--color-highlight);
    outline-offset: 2px;
  }
}

.gallery-item.loading {
  opacity: 1;
  background-color: var(--color-lightest-gray);
}

.gallery-item.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%);
  animation: shimmer 1.5s infinite;
}

.gallery-item.loaded {
  opacity: 1;
}

/* Hover effects; .hover-pinned shows the hovered look without a pointer (settings preview) */

.gallery-item.hover-zoom {
  transition: transform var(--transition-base);

  @media (hover: hover) {
    &:hover {
      transform: scale(1.03);
      z-index: 2;
    }
  }

  &.hover-pinned {
    transform: scale(1.03);
    z-index: 2;
  }
}

.gallery-item.hover-fade {
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: var(--color-bg);
    opacity: 0;
    transition: opacity var(--transition-base);
    pointer-events: none;
    z-index: 1;
  }

  @media (hover: hover) {
    &:hover::after { opacity: 0.6; }
  }

  &.hover-pinned::after { opacity: 0.6; }
}

.gallery-item.hover-darken {
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0);
    transition: background var(--transition-base);
    pointer-events: none;
    z-index: 1;
  }

  @media (hover: hover) {
    &:hover::after { background: rgba(0, 0, 0, 0.6); }
  }

  &.hover-pinned::after { background: rgba(0, 0, 0, 0.6); }
}

/* Image styles */
.gallery-item.style-rounded {
  border-radius: 12px;

  .gallery-img-natural,
  .gallery-img-cover {
    border-radius: 12px;
  }
}

.gallery-item.style-shadow {
  box-shadow: 0 4px 12px color-mix(in srgb, var(--color-text) 25%, transparent);
}

.gallery-item.style-border {
  border: 3px solid color-mix(in srgb, var(--color-text) 30%, var(--color-bg));
  border-radius: 0;

  .gallery-img-natural,
  .gallery-img-cover {
    border-radius: 0;
  }
}

@keyframes shimmer {
  0%   { left: -100%; }
  100% { left: 100%; }
}

/* Hidden preloader image (bg/grid mode) */
.gallery-img-preloader {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* Natural image — masonry: fills width, height follows aspect ratio */
.gallery-img-natural {
  display: block;
  width: 100%;
  height: auto;
  border-radius: var(--radius);
}

/* Cover image — rows: fills both dimensions */
.gallery-img-cover {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius);
}

/* Overlay — shared */
.gallery-overlay {
  position: absolute;
  color: var(--color-white);
  opacity: 0;
  transition: opacity var(--transition-base);
  z-index: 5;
  pointer-events: none;
  text-shadow:
    0 1px 3px rgba(0, 0, 0, 0.9),
    0 2px 8px rgba(0, 0, 0, 0.7),
    0 4px 16px rgba(0, 0, 0, 0.5);
}

.gallery-item {
  @media (hover: hover) {
    &:hover .gallery-overlay { opacity: 1; }
  }

  &.hover-pinned .gallery-overlay { opacity: 1; }
}

/* Bottom overlay */
.gallery-overlay-bottom {
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  background: none;
  text-align: center;
}

.gallery-overlay-bottom .overlay-title {
  font-family: var(--font-decorative);
  font-size: var(--font-xs);
  font-weight: 400;
  margin-bottom: 4px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.gallery-overlay-bottom .overlay-subtext {
  font-size: 12px;
  opacity: 0.9;
}

/* Full overlay */
.gallery-overlay-full {
  top: 0; left: 0; right: 0; bottom: 0;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-overlay-full .overlay-content {
  text-align: center;
  padding: 10px 5px;
  width: 100%;
  box-sizing: border-box;
}

.gallery-overlay-full .overlay-title {
  font-family: var(--font-decorative);
  font-size: 18px;
  font-weight: 400;
  margin-bottom: 12px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.3;
}

.gallery-overlay-full .overlay-subtext {
  font-size: var(--font-xs);
  opacity: 0.9;
}

.overlay-sold {
  font-size: var(--font-xs);
  margin-top: 4px;
}

.sold-badge {
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
  color: var(--color-white);
  background-color: var(--color-error);
  padding: 3px 6px 2px;
  border-radius: 3px;
  text-shadow: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.sold-dot {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 14px;
  height: 14px;
  background-color: var(--color-error);
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  z-index: 10;
}

.overlay-caption {
  font-size: 12px;
  opacity: 0.85;
  font-style: italic;
  line-height: 1.3;
}

.overlay-price {
  font-size: 13px;
  opacity: 0.9;
  margin-top: 4px;
}
</style>
